/* eslint-disable camelcase */
import 'leaflet/dist/leaflet.css';
import { EventDispatcher, createLeafletMap, fadeIn, fadeOut, isMobileView, l, lLink, lc, qs, qsa, setUrlParameter, sleep } from '@utils/toolbox';
import { ConnectApi } from '@js/plugins/connectApi';
import { dateFormatYMD } from '@utils/dateFns';
import { Modal } from '@components/modal';
import leaflet from 'leaflet';
import Supercluster from 'supercluster';

let resultModal;

export class PriceHunterMap {
	constructor (options = {}, element = '#map') {
		this.element = qs(element);
		if (!this.element) {
			console.error('No map element found');
			return;
		}
		this.map = null;
		this.data = null;
		this.showingList = {};
		this.hasInit = false;
		this.firstTime = true;
		this.CLICK_DRAG_TOLERANCE_PX = 10;
		this.clickStartPosition = null;
		this.isDragOverThreshold = false;
		this.isMapDragging = false;
		this.leafletMarkers = new Map();
		this.supercluster = new Supercluster({ radius: 70, maxZoom: 16 });
		this.options = {
			center: { lat: parseFloat(js_params.module.PriceHunter.latitude), lng: parseFloat(js_params.module.PriceHunter.longitude) },
			zoom: Number(js_params.module.PriceHunter.zoom),
			minZoom: 1,
			maxZoom: 10,
			...options
		};

		this.init();
	}

	reset () {
		const { center, zoom } = this.options;
		this.map.setView([center.lat, center.lng], zoom);
	}

	updateURLParameters () {
		const center = this.map.getCenter();
		const lat = center.lat.toFixed(6);
		const lng = center.lng.toFixed(6);
		const zoom = this.map.getZoom();

		let url = window.location.href;
		url = setUrlParameter(url, 'lat', lat);
		url = setUrlParameter(url, 'lng', lng);
		url = setUrlParameter(url, 'zoom', zoom);

		window.history.replaceState({}, '', url);
	}

	init () {
		if (this.hasInit) {
			return;
		}

		const { center, zoom, minZoom, maxZoom } = this.options;

		this.map = createLeafletMap(leaflet, this.element, {
			center: [center.lat, center.lng],
			zoom,
			minZoom,
			maxZoom,
			zoomControl: false,
			attributionControl: true
		});

		this.map.on('moveend zoomend', () => {
			this.refresh();
			this.updateZoomClass();
		});

		// Leaflet is synchronous — map is ready immediately
		this.mapLoaded = true;
		EventDispatcher.dispatch('map:ready');

		if (this.refreshOnReady) {
			this.refresh(true);
			this.refreshOnReady = false;
		}

		window.addEventListener('resize', () => {
			this.map.invalidateSize();
		});

		EventDispatcher.addEventListener('pricehunterMapDataFetched', data => this.onDataUpdated(data));

		EventDispatcher.addEventListener('mapToggleComplete', () => {
			this.map.invalidateSize();
		});

		this.hasInit = true;
		this.insertDesktopNoResults();
		this.setupDragDetection();
		this.updateZoomClass();
	}

	updateZoomClass () {
		const zoom = this.map.getZoom();
		this.element.classList.toggle('zoom-high', zoom > 6);
	}

	setupDragDetection () {
		this.isDragOverThreshold = false;
		this.clickStartPosition = null;

		const startDrag = (x, y) => {
			this.clickStartPosition = { x, y };
			this.isDragOverThreshold = false;
		};

		const endDrag = async () => {
			await sleep(50);
			this.isDragOverThreshold = false;
			this.clickStartPosition = null;
		};

		const duringDrag = (x, y) => {
			if (!this.clickStartPosition) {
				return;
			}
			const dx = x - this.clickStartPosition.x;
			const dy = y - this.clickStartPosition.y;
			if (Math.hypot(dx, dy) > this.CLICK_DRAG_TOLERANCE_PX) {
				this.isDragOverThreshold = true;
			}
		};

		this.element.addEventListener('mousedown', e => startDrag(e.clientX, e.clientY));
		this.element.addEventListener('mousemove', e => duringDrag(e.clientX, e.clientY));
		this.element.addEventListener('mouseup', endDrag);
		this.element.addEventListener('mouseleave', endDrag);

		this.element.addEventListener('touchstart', e => startDrag(e.touches[0].clientX, e.touches[0].clientY), { passive: true });
		this.element.addEventListener('touchmove', e => duringDrag(e.touches[0].clientX, e.touches[0].clientY), { passive: true });
		this.element.addEventListener('touchend', endDrag, { passive: true });
	}

	insertDesktopNoResults () {
		const html = this.getNoResultsHtml(true);
		this.element.insertAdjacentHTML('afterend', html);
	}

	onDataUpdated (data) {
		this.data = this.sortByCheapest(data);
		this.showingList = {};

		this.leafletMarkers.forEach(marker => marker.remove());
		this.leafletMarkers.clear();

		const points = this.data.map(item => ({
			type: 'Feature',
			geometry: { type: 'Point', coordinates: [Number(item.lng), Number(item.lat)] },
			properties: { ...item }
		}));
		this.supercluster.load(points);

		const { templateData } = this.parseResponse({ rows: data.slice(0, 10) });
		if (qs('.summary .summary__cheapest')) {
			qs('.summary .summary__cheapest').innerHTML = this.getTable(templateData, true);
		}
		fadeIn('.summary__cheapest');

		if (this.mapLoaded) {
			this.refresh(true);
		} else {
			this.refreshOnReady = true;
		}
	}

	sortByCheapest (data) {
		return data.sort(({ price: a }, { price: b }) => a - b);
	}

	refresh (fitbounds) {
		if (!this.data) {
			return;
		}
		this.element.style.visibility = 'visible';

		const bounds = this.map.getBounds();
		const zoom = Math.floor(this.map.getZoom());
		const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
		const clusters = this.supercluster.getClusters(bbox, zoom);

		const newKeys = new Set();

		clusters.forEach((cluster) => {
			const [lng, lat] = cluster.geometry.coordinates;
			let key, data, latlng;

			if (cluster.properties.cluster) {
				key = `cluster_${cluster.properties.cluster_id}`;
				const leaves = this.supercluster.getLeaves(cluster.properties.cluster_id, Infinity);
				const cheapest = leaves.reduce((a, b) => a.properties.price < b.properties.price ? a : b);
				data = cheapest.properties;
				latlng = leaflet.latLng(cheapest.geometry.coordinates[1], cheapest.geometry.coordinates[0]);
			} else {
				key = cluster.properties.outbound_to;
				data = cluster.properties;
				latlng = leaflet.latLng(lat, lng);
			}

			newKeys.add(key);

			if (!this.leafletMarkers.has(key)) {
				const marker = this.createPriceMarker(latlng, data);
				marker.addTo(this.map);
				this.leafletMarkers.set(key, marker);
				this.showingList[key] = marker;
			}
		});

		this.leafletMarkers.forEach((marker, key) => {
			if (!newKeys.has(key)) {
				marker.remove();
				this.leafletMarkers.delete(key);
				delete this.showingList[key];
			}
		});

		this.fixColors();

		const latlngs = this.data.map(d => leaflet.latLng(Number(d.lat), Number(d.lng)));
		const atLeastOneInBounds = latlngs.some(ll => bounds.contains(ll));

		if (this.firstTime || fitbounds === true) {
			this.firstTime = false;

			if (latlngs.length > 0 && latlngs.length < 10) {
				this.map.fitBounds(leaflet.latLngBounds(latlngs), { padding: [50, 50] });
			} else if (!atLeastOneInBounds) {
				this.reset();
			} else if (latlngs.length === 1) {
				this.map.setView(latlngs[0], 10);
			}
		}

		EventDispatcher.dispatch('pricehunter:refreshed');
		this.updateURLParameters();
	}

	createPriceMarker (latlng, data) {
		data = { ...data };
		data.city = data.localizedtocity || data.city;
		data.iata = data.iata ?? data.outbound_to;

		const responseId = data.id ?? data.response_id;
		const html = `
			<div class="price_tag" data-price="${data.price}" style="bottom:1.2rem;left:50%;transform:translateX(-50%)">
				<div class="arrow"></div>
				<button class="btn-close-tag icon-cross"></button>
				<div class="price">
					<a href="/${lLink('global.navigation.resultsLink')}?id=${responseId}&sorting=price" target="_blank">${lc(data.price)}</a>
				</div>
				<div class="info">${data.toname || ''}</div>
			</div>
		`;

		const icon = leaflet.divIcon({
			html,
			className: 'price_tag_leaflet_wrapper',
			iconSize: [0, 0],
			iconAnchor: [0, 0]
		});

		const marker = leaflet.marker(latlng, { icon });
		marker.data = data;

		marker.on('click', async (e) => {
			if (this.isDragOverThreshold) {
				return;
			}
			leaflet.DomEvent.stopPropagation(e);
			await this.onClick(e.originalEvent, { data });
		});

		return marker;
	}

	fixColors () {
		const priceElements = qsa('.price_tag', this.element);
		if (priceElements.length <= 1) {
			return;
		}

		let cheap = 9999999;
		let expensive = 0;

		for (const el of priceElements) {
			const p = parseInt(el.dataset.price);
			if (p < cheap) {
				cheap = p;
			}
			if (p > expensive) {
				expensive = p;
			}
		}

		for (const el of priceElements) {
			el.style.backgroundPositionX = (100 - (el.dataset.price - cheap) * 100 / (expensive - cheap)).toFixed(2) + '%';
		}
	}

	async onClick (e, bubble) {
		if (this.isMapDragging || this.isDragOverThreshold) {
			e.preventDefault();
			e.stopPropagation();
			return false;
		}
		e.preventDefault();
		const { header, body } = await this.getContent(bubble);
		const footer = `
<button class="js-close-modal abBtn abBtn--block">
	<span>${l('global.simplePhrases.close')}</span>
</button>
`;
		resultModal = new Modal({
			header: header,
			body: body,
			footer: footer,
			className: 'modal--priceHunter',
			hideFooter: false,
			hideOnSwipe: true,
			id: 'priceHunterModal',
			onClose: () => {
				resultModal = null;
			},
			resetScroll: true
		});

		resultModal.open();

		qs('.modal__header .icon-circle-cross').addEventListener('click', () => {
			resultModal.close();
		});

		return false;
	}

	getNoResultsHtml (isHidden = false) {
		return `
<div class="noResults" ${isHidden ? 'style="display: none"' : ''}>
	<div class="noResults__content">${l('showresult.info.allResultsAreFiltered')}</div>
	<button class="noResults__action abBtn abBtn--whiteBlue" data-action="filters-clear">
		${l('showresult.filter.removeBtnText')}
	</button>
</div>
`;
	}

	getTable (data, isExtended = false) {
		const { rows } = data;
		if (!rows.length) {
			return this.getNoResultsHtml();
		}

		const headerValues = isExtended
			? [l('pricehunter.map.price'), l('pricehunter.map.airports'), l('global.simplePhrases.dates', 'Datum')]
			: [l('pricehunter.map.price'), l('global.simplePhrases.From', 'Departure Airports'), l('pricehunter.map.outbound', 'Utresa'), l('pricehunter.map.inbound', 'Hemresa')];

		const bodyKeys = isExtended
			? ['departureArrivalText', 'departureArrivalDates']
			: ['departureText', 'formattedLeaveDate', 'formattedHomeDate'];

		return `
<table class="prices-tbl">
	<thead>
		<tr>
			${headerValues.map(text => `<th>${text}</th>`).join('')}
		</tr>
	</thead>
	<tbody>
	${rows.map((rows) => {
			return `
		<tr>
			<td><a class="price" href="${rows.link}">${rows.price}</a></td>
			${
	bodyKeys.map(key =>
		`<td><a href="${rows.link}">${rows[key]}</a></td>`
	).join('')
}
		</tr>
		`;
		}).join('')}
	</tbody>
</table>
`;
	}

	parseResponse (response) {
		const { localizedtocity, outbound_to, tocountry, localizedtocountry, to_city_id, to_country_id } = response.rows?.[0] || {};

		const rows = response.rows.map((row) => {
			return {
				link: window.pricehunter.getShowresultUrl(row),
				price: lc(row.price),
				departureArrivalText: `${row.fromname || row.localizedfromcity || row.fromcity}<br>${row.localizedtocity}, ${row.localizedtocountry}`,
				departureText: `${row.fromname || row.localizedfromcity || row.fromcity}`,
				departureArrivalDates: `${dateFormatYMD(new Date(row.leavedate))}<br> ${dateFormatYMD(new Date(row.homedate))}`,
				formattedLeaveDate: dateFormatYMD(new Date(row.leavedate)),
				formattedHomeDate: dateFormatYMD(new Date(row.homedate)),
				outbound_from: row.outbound_from,
				outbound_to: row.outbound_to,
				airports: row.outbound_from + ' - ' + row.outbound_to,
				localizedfromcity: row.localizedfromcity,
				localizedtocity: row.localizedtocity,
				tocountry: row.tocountry,
				localizedtocountry: row.localizedtocountry
			};
		});

		const templateData = {
			rows: rows
		};
		return {
			templateData,
			outbound_to,
			tocountry,
			localizedtocity,
			picture: `/api/destinationImage/${to_country_id}/${to_city_id}`,
			localizedtocountry,
			to_city_id,
			to_country_id
		};
	}

	getContent (bubble) {
		const data = window.pricehunter.getSearchParameters({ iatas_to: [bubble.data.iata], limit: 10 });

		return ConnectApi('PriceHunter', 'get', [data]).then((response) => {
			if (!response?.rows) {
				return l('error.search.technical');
			}
			response.rows = window.pricehunter.getFilteredResults(response.rows);
			const { templateData, outbound_to, tocountry, localizedtocity, localizedtocountry, picture } = this.parseResponse(response);

			const header = `<div class="title">${l('pricehunter.map.cheapestTo', 'Billigast till')} ${localizedtocity}, ${localizedtocountry} (${outbound_to})</div><span class="icon-circle-cross"></span>`;
			const body = `
<div class="priceHunterModal__container">
	<div class="prices">${this.getTable(templateData, false)}</div>
	${
	picture
		? `
<div class="priceHunterModal__image" style="">
	<div class="priceHunterModal__image__wrapper">
		<img src="${picture}" alt="${localizedtocity}">
		<div class="priceHunterModal__image__destination">
			<div class="priceHunterModal__image__city">
				<span>${localizedtocity}</span>
			</div>
			<div class="priceHunterModal__image__country">
				<span>${tocountry}</span>
			</div>
		</div>
	</div>
</div>`
		: ''
}
</div>
			`;

			const fullTemplate = header + body;
			return { body, header, fullTemplate };
		});
	}
}

let pricehunter_map;

const map_prices = {
	startup () {
		document.body.addEventListener('click', (e) => {
			const target = e.target;
			if (target.matches('table.prices-tbl td')) {
				qs('a', target).click();
			}

			if (target.closest('.icon-maximize')) {
				qs('.map-container').classList.add('maximized');
				sleep(100);
				pricehunter_map.map.invalidateSize();
			}

			if (target.closest('.icon-minimize')) {
				qs('.map-container').classList.remove('maximized');
				sleep(100);
				pricehunter_map.map.invalidateSize();
			}
		});

		const close = async () => {
			await fadeOut('.filters-top');
			document.body.classList.remove('modal-open');
			qs('#close-modal').innerHTML = l('global.simplePhrases.close');
			window.scrollTo(0, 0);
		};

		if (qs('[data-action="filters-close"]')) {
			qs('[data-action="filters-close"]').addEventListener('click', () => {
				close();
			});

			qs('.filters-top').addEventListener('click', (e) => {
				if (e.target.id !== 'filters-top' || !isMobileView()) {
					return;
				}
				close();
			});

			qs('.filtersMobileActivator').addEventListener('click', function () {
				if (document.body.classList.contains('modal-open')) {
					close();
				} else {
					document.body.classList.add('modal-open');
					fadeIn('.filters-top');
				}
			});
		}

		pricehunter_map.element.addEventListener('mouseenter', function (event) {
			const { target } = event;
			if (target.matches('div.price_tag')) {
				const zIndex = getComputedStyle(target).zIndex;
				if (zIndex === '1000000') {
					return;
				}
				target.dataset.zIndex = zIndex;
				target.style.zIndex = 1000000;
			} else if (target.matches('.q tr')) {
				target.classList.add('hovered');
			}
		}, true);

		pricehunter_map.element.addEventListener('mouseleave', function (event) {
			const { target } = event;
			if (target.matches('div.price_tag')) {
				const zIndex = target.dataset.zIndex;
				target.style.zIndex = zIndex;
				target.removeAttribute('data-z-index');
			} else if (target.matches('.prices-tbl tr')) {
				target.classList.remove('hovered');
			}
		}, true);
	}
};

export function initPriceHunterMap (options = {}, element = '#map') {
	if (pricehunter_map) {
		return pricehunter_map;
	}

	pricehunter_map = new PriceHunterMap(options, element);
	window.pricehunter_map = pricehunter_map;
	map_prices.startup();

	return Promise.resolve(pricehunter_map);
}
